/**
 * Android Permission Request Utility
 * Requests all necessary permissions at app startup
 * Uses Capacitor.Plugins directly for reliable native calls
 */

/**
 * Check if running in Capacitor native environment
 */
const isCapacitor = () => {
    return typeof window !== 'undefined' &&
        window.Capacitor &&
        window.Capacitor.isNativePlatform &&
        window.Capacitor.isNativePlatform();
};

/**
 * Request all necessary Android permissions at app startup
 * This shows native permission dialogs to the user
 */
export const requestAllPermissions = async () => {
    if (!isCapacitor()) {
        const results = { platform: 'web' };

        // Request location permission for web
        if (navigator.geolocation) {
            try {
                await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(
                        (position) => {
                            results.location = 'granted';
                            resolve(position);
                        },
                        (error) => {
                            results.location = error.code === 1 ? 'denied' : 'error';
                            resolve(null); // Don't reject, just continue
                        },
                        { timeout: 10000, enableHighAccuracy: true }
                    );
                });
            } catch (e) {
                results.location = 'error';
            }
        } else {
            results.location = 'unavailable';
        }

        // Request notification permission for web
        if ('Notification' in window && Notification.permission === 'default') {
            try {
                const notifResult = await Notification.requestPermission();
                results.notifications = notifResult;
            } catch (e) {
                results.notifications = 'error';
            }
        } else if ('Notification' in window) {
            results.notifications = Notification.permission;
        }

        return { success: true, ...results };
    }

    const results = {};

    // Wait a moment for the native bridge to be fully ready
    await new Promise(resolve => setTimeout(resolve, 500));

    try {
        // 1. Location Permission - Most important for GPS tracking
        try {
            const { Geolocation } = await import(/* webpackIgnore: true */ '@capacitor/geolocation');
            const locationStatus = await Geolocation.requestPermissions();
            results.location = locationStatus.location;
        } catch (e) {
            console.error('[Permissions] Location error:', e);
            results.location = 'error';
        }

        // 2. Camera Permission - For taking photos
        try {
            const { Camera } = await import(/* webpackIgnore: true */ '@capacitor/camera');
            const cameraStatus = await Camera.requestPermissions({ permissions: ['camera', 'photos'] });
            results.camera = cameraStatus.camera;
            results.photos = cameraStatus.photos;
        } catch (e) {
            console.error('[Permissions] Camera error:', e);
            results.camera = 'error';
        }

        // 3. Notifications Permission - CRITICAL for Android 13+
        try {
            const { LocalNotifications } = await import(/* webpackIgnore: true */ '@capacitor/local-notifications');


            // First check current status
            const currentStatus = await LocalNotifications.checkPermissions();


            if (currentStatus.display !== 'granted') {
                // Request permission - this shows the Android dialog
                const notifStatus = await LocalNotifications.requestPermissions();
                results.notifications = notifStatus.display;
            } else {
                results.notifications = 'granted';
            }

            // Create notification channel for Android
            try {
                await LocalNotifications.createChannel({
                    id: 'ep-eg-notifications',
                    name: 'إشعارات EP-EG',
                    description: 'إشعارات النظام والطلبات',
                    importance: 5, // MAX importance
                    visibility: 1, // PUBLIC
                    sound: 'default',
                    vibration: true,
                    lights: true,
                    lightColor: '#ea5f1f'
                });
            } catch (channelError) {
            }
        } catch (e) {
            console.error('[Permissions] Notifications error:', e);
            results.notifications = 'error';
        }

        // 4. Filesystem Permission - For saving files/PDFs
        try {
            const { Filesystem } = await import(/* webpackIgnore: true */ '@capacitor/filesystem');
            const fsStatus = await Filesystem.requestPermissions();
            results.filesystem = fsStatus.publicStorage;
        } catch (e) {
            console.error('[Permissions] Filesystem error:', e);
            results.filesystem = 'error';
        }

        return { success: true, platform: 'android', permissions: results };

    } catch (error) {
        console.error('[Permissions] Overall error:', error);
        return { success: false, error: error.message, permissions: results };
    }
};

/**
 * Check if a specific permission is granted
 */
export const checkPermission = async (permissionType) => {
    if (!isCapacitor()) return true;

    try {
        switch (permissionType) {
            case 'location': {
                const { Geolocation } = await import(/* webpackIgnore: true */ '@capacitor/geolocation');
                const status = await Geolocation.checkPermissions();
                return status.location === 'granted';
            }
            case 'camera': {
                const { Camera } = await import(/* webpackIgnore: true */ '@capacitor/camera');
                const status = await Camera.checkPermissions();
                return status.camera === 'granted';
            }
            case 'notifications': {
                const { LocalNotifications } = await import(/* webpackIgnore: true */ '@capacitor/local-notifications');
                const status = await LocalNotifications.checkPermissions();
                return status.display === 'granted';
            }
            case 'storage': {
                const { Filesystem } = await import(/* webpackIgnore: true */ '@capacitor/filesystem');
                const status = await Filesystem.checkPermissions();
                return status.publicStorage === 'granted';
            }
            default:
                return false;
        }
    } catch (e) {
        console.error(`[Permissions] Check ${permissionType} error:`, e);
        return false;
    }
};

export default { requestAllPermissions, checkPermission };
